import { useState } from "react";
import { MdCheckCircle, MdClose, MdMoreHoriz } from "react-icons/md";
import {
  FormattedMessage,
  FormattedNumber,
  defineMessages,
  useIntl,
} from "react-intl";
import { graphql, usePaginationFragment } from "react-relay";
import { Link } from "react-router-dom";
import Button, { ToolbarButton } from "./Button";
import EntityProfilePic from "./EntityProfilePic";
import * as Dialog from "./Dialog";
import Markdown from "./Markdown";
import Note from "./Note";
import {
  SubmissionApprovalsFragment$data,
  SubmissionApprovalsFragment$key,
} from "./__generated__/SubmissionApprovalsFragment.graphql";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/kit/ui/tooltip";

const Fragment = graphql`
  fragment SubmissionApprovalsFragment on ProjectVersion
  @refetchable(queryName: "SubmissionApprovalsPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
  ) {
    entity {
      username
      displayName
    }
    approvals(first: $count, after: $cursor)
      @connection(key: "SubmissionApprovalsFragment_approvals")
      @ifAllowed {
      edges {
        cursor
        node {
          accept
          notes
          scores {
            id
            value
            competitionScoringCriterion {
              id
              title
              description
            }
          }
          entity {
            username
            displayName
            ... on User {
              jobTitle
            }
            ...EntityProfilePicFragment @arguments(thumbnail: true)
          }
        }
      }
    }
  }
`;

export interface SubmissionApprovalsProps {
  submission: SubmissionApprovalsFragment$key;
}

export default function SubmissionApprovals(props: SubmissionApprovalsProps) {
  const intl = useIntl();
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    Fragment,
    props.submission,
  );
  const [dialogOpenAt, setDialogOpenAt] = useState<number | undefined>();

  if (!data.approvals || data.approvals.edges.length <= 0) {
    return null;
  }

  return (
    <>
      <div className="flex flex-row flex-wrap gap-1">
        {data.approvals.edges.map(({ node, cursor }, index) => (
          <button
            key={cursor}
            className="inline-flex flex-col gap-1 items-center relative px-2"
            title={intl.formatMessage(
              node.accept ? messages.approved : messages.rejected,
              { username: data.entity.username, jury: node.entity.username },
            )}
            onClick={(_evt) => {
              setDialogOpenAt(index);
            }}
          >
            <EntityProfilePic size="10" entity={node.entity} />
            <span>{node.entity.displayName}</span>

            <span className="absolute top-0 right-0">
              {node.accept ? (
                <MdCheckCircle className="text-green-600" />
              ) : (
                <MdClose className="text-red-600" />
              )}
            </span>
          </button>
        ))}

        {hasNext && (
          <div className="flex items-center">
            <ToolbarButton
              size="sm"
              disabled={isLoadingNext}
              onClick={(_evt) => {
                loadNext(10);
              }}
            >
              <MdMoreHoriz />
            </ToolbarButton>
          </div>
        )}
      </div>

      {typeof dialogOpenAt !== "undefined" && (
        <ApprovalDialog
          data={data}
          edge={dialogOpenAt}
          onClosed={() => setDialogOpenAt(undefined)}
        />
      )}
    </>
  );
}

interface ApprovalDialogContentProps {
  data: SubmissionApprovalsFragment$data;
  edge: number;
  onClosed(): void;
}

function ApprovalDialog(props: ApprovalDialogContentProps) {
  const entity = props.data.entity;
  const {
    node: { accept, notes, scores, entity: jury },
  } = props.data.approvals.edges[props.edge];
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) {
          props.onClosed();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content className="xl:min-w-[720px] max-h-[67%]">
          <div className="flex flex-row items-center px-4 md:py-2 py-4">
            <Dialog.Title className="flex-1">
              <FormattedMessage
                {...(accept ? messages.approved : messages.rejected)}
                values={{
                  username: (
                    <Link to={`/${entity.username}`}>{entity.displayName}</Link>
                  ),
                  jury: (
                    <Link to={`/${jury.username}`}>{jury.displayName}</Link>
                  ),
                }}
              />
            </Dialog.Title>

            <Dialog.Close />
          </div>

          {accept &&
            scores
              .filter(({ value }) => typeof value === "number")
              .map(
                ({
                  id,
                  value,
                  competitionScoringCriterion: { title, description },
                }) => {
                  const note = (
                    <Note>
                      {title}:{" "}
                      <strong>
                        <FormattedNumber value={value!} />
                      </strong>
                    </Note>
                  );
                  return (
                    <div key={id}>
                      {description ? (
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger>{note}</TooltipTrigger>
                          <TooltipContent>{description}</TooltipContent>
                        </Tooltip>
                      ) : (
                        note
                      )}
                    </div>
                  );
                },
              )}

          {notes ? (
            <div className="overflow-auto">
              <Markdown>{notes}</Markdown>
            </div>
          ) : (
            <p className="text-gray-400">
              <FormattedMessage defaultMessage="No notes left" />
            </p>
          )}

          <Dialog.Close.Mobile className="mt-8 w-full" asChild>
            <Button kind="secondary" className="w-full">
              <FormattedMessage defaultMessage="Close" />
            </Button>
          </Dialog.Close.Mobile>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const messages = defineMessages({
  approved: {
    defaultMessage: "{jury} has accepted {username}'s submission",
  },
  rejected: {
    defaultMessage: "{jury} has rejected {username}'s submission",
  },
});
